import { renderCellHTML } from 'common/DataTypeFormatter';
import {
  FilterDataType,
  type NumberFilterArgument,
  type SoqlFilter,
} from 'common/components/FilterBar/SoqlFilter';
import { type ViewColumn } from 'common/types/viewColumn';

/**
 * Pre-formats number filter values on the main thread so the PDF "Filters:" section
 * renders them the same way the filter bar does (WYSIWYG) — e.g. a currency column
 * shows `$200.00`, a percentage column `13%`, etc.
 *
 * The PDF converter runs in a web worker that has no `window`, so it cannot call the
 * locale-aware DataTypeFormatter the bar uses. We run that formatter here (main thread,
 * `window` available) and stash the results in the filter argument's `formatted*` fields;
 * the worker renders those and leaves the raw values untouched. Non-number filters and
 * columns we can't resolve are returned unchanged.
 *
 * `viewColumns` must be the full dataset column metadata (which carries the configured
 * precision style), not the vif's dimension.columns snapshot.
 */
export const formatNumberFilterValuesForDisplay = (
  filters: SoqlFilter[] | undefined,
  viewColumns: ViewColumn[] | undefined,
): SoqlFilter[] | undefined => {
  if (!filters) {
    return filters;
  }

  return filters.map((filter): SoqlFilter => {
    if (filter.dataTypeName !== FilterDataType.NUMBER) {
      return filter;
    }

    const fieldName = filter.columns[0]?.fieldName;
    const column = viewColumns?.find((viewColumn) => viewColumn.fieldName === fieldName);
    const args = (filter as { arguments?: NumberFilterArgument }).arguments;
    if (!column || !args) {
      return filter;
    }

    // renderCellHTML applies the column's render type + format (currency, percent,
    // decimals, separators) exactly as the bar does. it returns plain text for numeric
    // columns, which is what the PDF needs.
    const format = (raw: string | number | undefined): string | undefined =>
      raw == null || raw === '' ? undefined : renderCellHTML(raw, column);

    const formattedArgs = {
      ...args,
      formattedValue: format(args.value),
      formattedStart: format(args.start),
      formattedEnd: format(args.end),
    };

    return { ...filter, arguments: formattedArgs } as SoqlFilter;
  });
};
